import {isNumber} from 'lodash';

import {SelectionLocation} from '../../../libs/shared/core/src/helpers/browser/domSelectionHelpers';
import {
  AnnotationCommentHighlightModel,
  AnnotationMessageHighlightModel,
  AnnotationTopicModel,
  HighlightAnnotationModel,
} from '../../models/api/annotationModel';
import {
  AnnotationLinkedResourcesEnum,
  AnnotationSourcesEnum,
  AnnotationTypesEnum,
  RAnnotationLinkedResourcesEnum,
  SelectionAnnotationTemplate,
} from '../../models/api/annotationTypes';

export function buildAnnotationFromTemplate(
  template: SelectionAnnotationTemplate,
  commentUid: string,
): AnnotationMessageHighlightModel | AnnotationCommentHighlightModel {
  const {location, type} = template;
  const {startIndex, endIndex, text} = location;

  if (type === AnnotationTypesEnum.HIGHLIGHT_COMMENT) {
    return {
      type: AnnotationTypesEnum.HIGHLIGHT_COMMENT,
      startIndex,
      endIndex,
      text,
      textBefore: location.textBefore,
      textAfter: location.textAfter,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      clientSideUid: buildAnnotationClientSideUid(AnnotationLinkedResourcesEnum.COMMENT, commentUid),
    };
  }

  return {
    type: AnnotationTypesEnum.HIGHLIGHT_MESSAGE,
    startIndex,
    endIndex,
    text,
    textBefore: location.textBefore,
    textAfter: location.textAfter,
    source: AnnotationSourcesEnum.LIGHT_BODY,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    clientSideUid: buildAnnotationClientSideUid(AnnotationLinkedResourcesEnum.COMMENT, commentUid),
  };
}

/** Test if the selection is fully contained within an annotation. */
export function isSelectionInsideAnnotation(
  location: SelectionLocation | undefined,
  annotations:
    | ReadonlyArray<AnnotationCommentHighlightModel | AnnotationMessageHighlightModel | AnnotationTopicModel>
    | undefined,
) {
  if (!location || !annotations) {
    return false;
  }

  const {startIndex, endIndex} = location;

  return annotations.some(
    (annotation) => annotation.startIndex <= startIndex && annotation.endIndex >= endIndex,
  );
}

const ANNOTATIONS_PER_LOCATION_LIMIT = 3;
export function canLocationAcceptMoreAnnotations(
  location: SelectionLocation | undefined,
  annotations:
    | ReadonlyArray<AnnotationCommentHighlightModel | AnnotationMessageHighlightModel | AnnotationTopicModel>
    | undefined,
) {
  if (!location || !annotations) {
    return false;
  }

  const {startIndex, endIndex} = location;

  const annotationsInLocation = annotations.filter(
    (annotation) => annotation.startIndex <= startIndex && annotation.endIndex >= endIndex,
  );

  return annotationsInLocation.length < ANNOTATIONS_PER_LOCATION_LIMIT;
}

export function buildAnnotationClientSideUid(
  linkedResource: AnnotationLinkedResourcesEnum,
  linkedResourceIdOrUid: string | number,
  annotationId?: number,
) {
  return `${linkedResource}#${linkedResourceIdOrUid}${(annotationId && `#${annotationId}`) || ''}`;
}

export function isAnnotationUid(maybeUid: string | undefined): boolean {
  if (!maybeUid) {
    return false;
  }
  const [linkedResource, linkedResourceId, annotationId] = maybeUid.split('#');

  return (
    RAnnotationLinkedResourcesEnum.is(linkedResource) &&
    isNumber(Number(linkedResourceId)) &&
    (annotationId === undefined || !isNaN(Number(annotationId)))
  );
}

export function filterHighlightAnnotationsForMessageContent(
  annotations: ReadonlyArray<HighlightAnnotationModel> | undefined,
) {
  if (!annotations) {
    return [];
  }
  return annotations.filter(
    (a) =>
      (a.type === AnnotationTypesEnum.HIGHLIGHT_MESSAGE || a.type === AnnotationTypesEnum.TOPIC) &&
      a.source === AnnotationSourcesEnum.LIGHT_BODY,
  );
}

export function filterHighlightAnnotationsForMessageSubject(
  annotations: ReadonlyArray<HighlightAnnotationModel> | undefined,
) {
  if (!annotations) {
    return [];
  }
  return annotations.filter(
    (a) =>
      (a.type === AnnotationTypesEnum.HIGHLIGHT_MESSAGE || a.type === AnnotationTypesEnum.TOPIC) &&
      a.source === AnnotationSourcesEnum.SUBJECT,
  );
}
